import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import axios from 'axios';
import { ChatMessage, SensorData } from '../types';
import { AuthService } from '../services/AuthService';
import {
  OperationsGuide,
  OperationsGuideAction,
  OperationsReadiness,
} from './OperationsGuide';

interface ChatbotProps {
  sensorData: SensorData;
  activeTab?: string;
  dataSource?: 'simulation' | 'historical' | 'live' | 'energyplus';
  observedAt?: string | null;
  onNavigateTab?: (tab: string) => void;
  onDataSourceModeChange?: (mode: 'historical' | 'live' | 'energyplus') => void;
}

interface RlContext {
  connected: boolean;
  totalSteps: number;
  episode: number;
  bufferSize: number;
  latestReward: number | null;
  isTraining: boolean;
  ahuSaTemp: number | null;
  chwOn: boolean | null;
  chwSp: number | null;
  cwSp: number | null;
  bt4Sp: number | null;
  bufferTankSp: number | null;
  topFeatures: Array<{ agent: string; feature: string; contrib: number }>;
}

interface BriefingSnapshot {
  dataSource: string;
  temp: number;
  humidity: number;
  power: number;
  outdoorTemp: number;
  co2: number;
  rlConnected: boolean;
  ahuSaTemp: number | null;
  chwOn: boolean | null;
  chwSp: number | null;
  cwSp: number | null;
  bt4Sp: number | null;
  bufferTankSp: number | null;
}

interface OpenAiUsageSummary {
  date: string;
  requestCount: number;
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  lastRequestAt: string | null;
  mode: 'manual_only';
  limits: { requests: number; tokens: number };
  remaining: { requests: number; tokens: number };
}

interface ChatCapabilities {
  provider: 'local_skills' | 'local_skills+openai_optional' | string;
  configured: boolean;
  responseRouting: string;
  policy?: {
    controlToolsEnabled?: boolean;
    serviceRecoveryEnabled?: boolean;
    serviceRecoveryRequiresAdmin?: boolean;
    conciseBemsQuestionsUseLocalSkills?: boolean;
    localBriefingUsesOpenAiTokens?: boolean;
  };
}

interface LauncherPosition {
  x: number;
  y: number;
}

interface ControlAdvisoryProposal {
  id: string;
  status: 'pending' | 'accepted' | 'rejected' | 'expired' | 'cancelled';
  step_index: number;
  simulation_timestamp: string;
  expires_at: string;
  actions: {
    ahu_supply_air_setpoint_c: number;
    chiller_on: boolean;
    chilled_water_setpoint_c: number;
    condenser_water_setpoint_c: number;
    buffer_tank_4_setpoint_c: number;
  };
}

interface RecoveryServiceStatus {
  id: 'gateway' | 'tracking' | 'validation' | 'energyplus';
  label: string;
  port: number;
  stateful: boolean;
  status: 'starting' | 'healthy' | 'degraded' | 'offline' | 'recovering' | 'attention';
  consecutiveFailures: number;
  lastSuccessAt: string | null;
  lastFailureAt: string | null;
  lastError: string | null;
  lastKnownActive: boolean | null;
  lastRecoveryAt: string | null;
  lastRecoveryResult: string | null;
  actionRequired: string | null;
}

interface ServiceRecoveryStatus {
  schemaVersion: string;
  overall: 'starting' | 'healthy' | 'degraded' | 'recovering' | 'attention';
  autoRecoveryEnabled?: boolean;
  watchdogConnected?: boolean;
  maintenance?: boolean;
  updatedAt?: string;
  services: RecoveryServiceStatus[];
  latestEvent?: {
    id: string;
    timestamp: string;
    type: string;
    serviceId: string;
    message: string;
  } | null;
  error?: string;
}

type ManualControlDraft = ControlAdvisoryProposal['actions'];

const parseManualControlRequest = (message: string): Partial<ManualControlDraft> | null => {
  if (!/(변경|설정|맞춰|적용|제어)/.test(message)) return null;
  const result: Partial<ManualControlDraft> = {};
  const extract = (pattern: RegExp) => {
    const match = message.match(pattern);
    return match ? Number(match[1]) : null;
  };
  const ahu = extract(/(?:급기|ahu)[^0-9-]*(\d+(?:\.\d+)?)/i);
  const chw = extract(/(?:냉수|chw)[^0-9-]*(\d+(?:\.\d+)?)/i);
  const cw = extract(/(?:냉각수|cw)[^0-9-]*(\d+(?:\.\d+)?)/i);
  const bt4 = extract(/(?:bt\s*4|버퍼탱크\s*4)[^0-9-]*(\d+(?:\.\d+)?)/i);
  if (ahu != null) result.ahu_supply_air_setpoint_c = ahu;
  if (chw != null) result.chilled_water_setpoint_c = chw;
  if (cw != null) result.condenser_water_setpoint_c = cw;
  if (bt4 != null) result.buffer_tank_4_setpoint_c = bt4;
  if (/(?:냉동기|chiller).*(?:켜|on|가동)/i.test(message)) result.chiller_on = true;
  if (/(?:냉동기|chiller).*(?:꺼|off|정지)/i.test(message)) result.chiller_on = false;
  return Object.keys(result).length ? result : null;
};

const validateManualControlDraft = (actions: ManualControlDraft) => {
  const checks: Array<[number, number, number, string]> = [
    [actions.ahu_supply_air_setpoint_c, 13, 18, '급기온도'],
    [actions.chilled_water_setpoint_c, 7, 13, '냉수온도'],
    [actions.condenser_water_setpoint_c, 23, 34, '냉각수온도'],
    [actions.buffer_tank_4_setpoint_c, 14, 20, 'BT4 설정온도'],
  ];
  const invalid = checks.find(([value, min, max]) => !Number.isFinite(value) || value < min || value > max);
  return invalid ? `${invalid[3]}는 ${invalid[1]}~${invalid[2]}°C 범위에서 설정해 주세요.` : null;
};

type SafeDashboardTab =
  | 'overview'
  | 'digital-twin'
  | 'loop-water'
  | 'loop-air'
  | 'ai-control'
  | 'ai-model'
  | 'reporting'
  | 'settings';

type ReportingCommand = {
  category?: 'operations' | 'overview' | 'explorer' | 'evidence' | 'tracking' | 'energy' | 'systems' | 'comfort' | 'weather' | 'rl';
  metrics?: string[];
  stepSec?: 300 | 600 | 1200 | 3600;
  condition?: 'all' | 'businessHours' | 'systemOn' | 'hvacActive';
};

type SafeDashboardAction = {
  tab: SafeDashboardTab;
  reporting?: ReportingCommand;
  reply: string;
};

const COPILOT_NAME = '범비';
const COPILOT_INTRODUCTION = '안녕하세요. 운영 도우미 범비예요. 무엇을 도와드릴까요?';
const OPERATIONS_GUIDE_VERSION = '2026-09-30-v1';
const ENERGYPLUS_GUIDE_ACTION_KEY = 'aees:energyplus-guide-action';

const identityReply = (message: string) => {
  const normalized = message.trim().replace(/[?.!~]+$/g, '');
  return /자기\s*소개|네\s*이름|너.*이름|이름.*뭐|누구(?:야|세요|니)|^(?:안녕|안녕하세요|반가워)$/i.test(normalized)
    ? COPILOT_INTRODUCTION
    : null;
};

const VIEW_LABELS: Record<string, string> = {
  overview: '공간 트윈',
  'digital-twin': '트윈 분석',
  'loop-water': '열원 시스템',
  'loop-air': '공조 시스템',
  'ai-control': 'AI 제어 분석',
  'ai-explain': 'AI 판단 근거',
  'ai-model': '정책 튜닝',
  reporting: '운영 리포트',
  settings: '환경 설정',
};

const EVIDENCE_STATUS_LABELS: Record<string, string> = {
  grounded: '근거 확인',
  limited: '제한된 근거',
  available: '확인됨',
  valid: '정상',
  running: '실행 중',
  completed: '완료 결과',
  authoritative_project_rule: '프로젝트 규칙',
  configured_only: '설정값만',
  degraded: '품질 주의',
  stale: '지연',
  unavailable: '사용 불가',
  unverified: '미검증',
  no_action: 'Action 없음',
};

const formatEvidenceTime = (value?: string | null) => {
  if (!value) return '시각 없음';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString('ko-KR', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

type QuickPrompt = {
  label: string;
  prompt: string;
  kind: 'local' | 'setpoint';
};

const QUICK_PROMPTS: QuickPrompt[] = [
  { label: '지금 상태를 쉽게 요약해줘.', prompt: '지금 상태를 쉽게 요약해줘.', kind: 'local' },
  { label: '이상 징후와 점검 순서를 알려줘.', prompt: '이상 징후와 점검 순서를 알려줘.', kind: 'local' },
  { label: '오늘 운영 보고서를 요약해줘.', prompt: '오늘 운영 보고서를 요약해줘.', kind: 'local' },
  { label: 'Baseline 대비 절감효과를 알려줘.', prompt: 'Baseline 대비 절감효과를 알려줘.', kind: 'local' },
  { label: 'AI 제어값이 왜 이렇게 나왔는지 설명해줘.', prompt: 'AI 제어값이 왜 이렇게 나왔는지 설명해줘.', kind: 'local' },
];

const ENERGYPLUS_SETPOINT_PROMPTS: QuickPrompt[] = [
  { label: '급기 16°C 설정', prompt: '급기온도를 16도로 변경해줘.', kind: 'setpoint' },
  { label: '냉수 10°C 설정', prompt: '냉수온도를 10도로 변경해줘.', kind: 'setpoint' },
  { label: '냉각수 28°C 설정', prompt: '냉각수온도를 28도로 변경해줘.', kind: 'setpoint' },
  { label: 'BT4 16°C 설정', prompt: 'BT4 설정온도를 16도로 변경해줘.', kind: 'setpoint' },
  {
    label: '설정온도 일괄 입력',
    prompt: '급기 16도, 냉수 10도, 냉각수 28도, BT4 16도로 설정해줘.',
    kind: 'setpoint',
  },
];

const LOCAL_COMMAND_EXAMPLES = [
  { label: '운영 상태', prompt: '지금 상태를 쉽게 요약해줘.' },
  { label: 'Tracking', prompt: 'Tracking 정확도와 현재 상태를 알려줘.' },
  { label: 'AI 제어', prompt: '현재 AI 제어값과 근거를 알려줘.' },
  { label: '리포트', prompt: '오늘 운영 보고서를 요약해줘.' },
  { label: '서버 상태', prompt: '현재 서버 상태와 복구 여부를 알려줘.' },
  { label: '화면 이동', prompt: '공조 계통 화면으로 이동해줘.' },
] as const;

const OPENAI_COMMAND_EXAMPLES = [
  { label: '자유 질문', prompt: '현재 운영 상태에서 추가로 고려할 점을 설명해줘.' },
  { label: '상세 해석', prompt: 'Tracking과 AI 제어 결과의 관계를 자세히 해석해줘.' },
  { label: '비교 설명', prompt: '현재 결과를 일반적인 BEMS 운영 관점과 비교해 설명해줘.' },
] as const;

type PetMood = 'idle' | 'listening' | 'thinking' | 'excited' | 'alert' | 'sleepy';

const PET_MOOD_LABELS: Record<PetMood, string> = {
  idle: '대기',
  listening: '듣는 중',
  thinking: '생각 중',
  excited: '새 알림',
  alert: '확인 필요',
  sleepy: '연결 대기',
};

const PET_SPRITES: Record<PetMood, string> = {
  idle: '/assets/aees-tiger-assistant.png',
  listening: '/assets/aees-tiger-listening.png',
  thinking: '/assets/aees-tiger-thinking.png',
  excited: '/assets/aees-tiger-excited.png',
  alert: '/assets/aees-tiger-alert.png',
  sleepy: '/assets/aees-tiger-sleepy.png',
};

const PET_REACTION_DURATION_MS: Record<PetMood, number> = {
  idle: 900,
  listening: 1_200,
  thinking: 1_800,
  excited: 1_800,
  alert: 2_600,
  sleepy: 2_200,
};

const initialRlContext: RlContext = {
  connected: false,
  totalSteps: 0,
  episode: 0,
  bufferSize: 0,
  latestReward: null,
  isTraining: false,
  ahuSaTemp: null,
  chwOn: null,
  chwSp: null,
  cwSp: null,
  bt4Sp: null,
  bufferTankSp: null,
  topFeatures: [],
};

const finite = (value: number | null | undefined, fallback = 0) => (
  Number.isFinite(value) ? Number(value) : fallback
);

const readStoredLauncherPosition = (): LauncherPosition | null => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem('aees:pet-launcher-position');
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<LauncherPosition>;
    if (!Number.isFinite(parsed.x) || !Number.isFinite(parsed.y)) return null;
    return { x: Number(parsed.x), y: Number(parsed.y) };
  } catch {
    return null;
  }
};

const includesAny = (text: string, keywords: string[]) => keywords.some(keyword => text.includes(keyword));
const REPORTING_COMMAND_STORAGE_KEY = 'aees:pending-reporting-command';
const OPERATIONS_REPORT_SNAPSHOT_STORAGE_KEY = 'aees:operations-report-summary';
const CHAT_HISTORY_STORAGE_PREFIX = 'aees:copilot-history';

const chatHistoryStorageKey = () => {
  const user = AuthService.getUser();
  return `${CHAT_HISTORY_STORAGE_PREFIX}:${user?.username ?? 'user'}`;
};

const readStoredMessages = (): ChatMessage[] => {
  if (typeof window === 'undefined') return [{ role: 'assistant', content: COPILOT_INTRODUCTION }];
  try {
    const parsed = JSON.parse(window.sessionStorage.getItem(chatHistoryStorageKey()) || '[]');
    if (!Array.isArray(parsed)) throw new Error('invalid history');
    const messages = parsed.filter(item => (
      (item?.role === 'user' || item?.role === 'assistant')
      && typeof item?.content === 'string'
    )).slice(-40);
    return messages.length ? messages : [{ role: 'assistant', content: COPILOT_INTRODUCTION }];
  } catch {
    return [{ role: 'assistant', content: COPILOT_INTRODUCTION }];
  }
};

const readStoredOperationsReport = () => {
  if (typeof window === 'undefined') return null;
  try {
    const parsed = JSON.parse(window.sessionStorage.getItem(OPERATIONS_REPORT_SNAPSHOT_STORAGE_KEY) || 'null');
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
};

const resolveReportingCommand = (text: string): ReportingCommand | null => {
  if (!includesAny(text, ['리포트', '보고서', '그래프', 'csv', '저장', '내보내기', '다운로드'])) {
    return null;
  }

  const command: ReportingCommand = {};
  const wantsExplorer = includesAny(text, ['맞춤', '선택', '골라', '비교', '같이', '동시에']);
  if (includesAny(text, ['운영 보고서', '운영 리포트', '종합 보고서', '현장 보고', '보고서 작성', '보고서 만들어'])) {
    command.category = 'operations';
  } else if (includesAny(text, ['csv', '저장', '내보내기', '다운로드'])) {
    command.category = 'overview';
  } else if (includesAny(text, ['tracking', '예측 정확도', '검증'])) {
    command.category = 'tracking';
  } else if (includesAny(text, ['승인', '증거', '근거 자료'])) {
    command.category = 'evidence';
  } else if (includesAny(text, ['에너지', '전력', '소비량', 'kwh', 'kw', '계량'])) {
    command.category = wantsExplorer ? 'explorer' : 'energy';
  } else if (includesAny(text, ['시스템 온도', '설비 온도', '급기', '환기', 'ahu', '히트펌프', '열원'])) {
    command.category = wantsExplorer ? 'explorer' : 'systems';
  } else if (includesAny(text, ['온도', '습도', 'co2', '공기질'])) {
    command.category = wantsExplorer ? 'explorer' : 'comfort';
  } else {
    command.category = 'overview';
  }

  const metrics = new Set<string>();
  if (includesAny(text, ['전력', 'kw', '부하'])) {
    ['hvacKw', 'ahuKw', 'plantKw'].forEach(metric => metrics.add(metric));
  }
  if (includesAny(text, ['에너지', '소비량', 'kwh'])) {
    ['hvacKwh', 'ahuKwh', 'plantKwh'].forEach(metric => metrics.add(metric));
  }
  if (includesAny(text, ['온도', '급기', '환기', '외기'])) {
    ['zoneTemp', 'returnAirTemp', 'supplyAirTemp', 'outdoorTemp'].forEach(metric => metrics.add(metric));
  }
  if (includesAny(text, ['히트펌프', '열원', '지중', '부하측'])) {
    ['hp1GeoSupply', 'hp1GeoReturn', 'hp1LoadSupply', 'hp1LoadReturn'].forEach(metric => metrics.add(metric));
  }
  if (includesAny(text, ['습도', 'co2', '공기질'])) {
    ['humidity', 'co2'].forEach(metric => metrics.add(metric));
  }
  if (metrics.size) {
    command.metrics = Array.from(metrics);
    if (wantsExplorer) command.category = 'explorer';
  }

  if (includesAny(text, ['5분', '자세', '세부', '촘촘'])) command.stepSec = 300;
  else if (text.includes('10분')) command.stepSec = 600;
  else if (text.includes('20분')) command.stepSec = 1200;
  else if (includesAny(text, ['1시간', '한시간', '요약'])) command.stepSec = 3600;

  if (includesAny(text, ['업무시간', '근무시간'])) command.condition = 'businessHours';
  else if (includesAny(text, ['운전 중', '운전중', '시스템 on', 'system on'])) command.condition = 'systemOn';
  else if (includesAny(text, ['전력 발생', '부하 발생', 'hvac active'])) command.condition = 'hvacActive';

  return command;
};

const resolveSafeDashboardAction = (message: string): SafeDashboardAction | null => {
  const text = message.toLowerCase();
  const reporting = resolveReportingCommand(text);
  if (reporting) {
    const target = reporting.category === 'explorer'
      ? '맞춤 그래프'
      : reporting.category === 'operations'
        ? '운영 보고서'
      : reporting.category === 'energy'
        ? '전력·에너지'
        : reporting.category === 'systems'
          ? '설비 온도'
          : reporting.category === 'comfort'
            ? '온도·공기질'
            : reporting.category === 'tracking'
              ? '예측 정확도'
              : reporting.category === 'evidence'
                ? '승인 자료'
                : '운영 요약';
    return {
      tab: 'reporting',
      reporting,
      reply: `운영 리포트의 ${target} 화면으로 이동했습니다. BEMS 제어값이나 코드는 변경하지 않았습니다.`,
    };
  }

  const tabRules: Array<{ tab: SafeDashboardTab; label: string; keywords: string[] }> = [
    { tab: 'loop-water', label: '열원 계통', keywords: ['열원', '냉동기', '냉각탑', '히트펌프', '버퍼', 'chw', '냉수'] },
    { tab: 'loop-air', label: '공조 계통', keywords: ['공조', 'ahu', '급기', '환기', '팬', 'vav', '공기'] },
    { tab: 'ai-control', label: 'AI 제어 분석', keywords: ['ai 제어', '강화학습', 'rl', 'action', 'state', 'shap', 'xai', '제어 분석'] },
    { tab: 'ai-model', label: '정책 튜닝', keywords: ['정책 튜닝', '재학습', '파인튜닝', '학습 탭', '모델 학습'] },
    { tab: 'digital-twin', label: '트윈 분석', keywords: ['트윈 분석', 'tracking', 'energyplus', '예측', '시뮬레이션'] },
    { tab: 'settings', label: '환경 설정', keywords: ['센서 연결', 'bems 연결', '포인트', '매핑', '설정', 'api key'] },
    { tab: 'overview', label: '공간 트윈', keywords: ['공간 트윈', '메인', '홈', '전체 상태'] },
  ];

  const match = tabRules.find(rule => includesAny(text, rule.keywords));
  if (!match) return null;
  return {
    tab: match.tab,
    reply: `${match.label} 화면으로 이동했습니다. BEMS 제어값이나 코드는 변경하지 않았습니다.`,
  };
};

const fmt = (value: number | null | undefined, digits = 1) => (
  Number.isFinite(value) ? Number(value).toFixed(digits) : '--'
);

const hasOperationalAlert = (snapshot: BriefingSnapshot) => (
  (snapshot.temp > 0 && (snapshot.temp < 23.5 || snapshot.temp > 26.5))
  || snapshot.power > 120
  || snapshot.co2 > 1000
);

const parseChillerAction = (values?: number[]) => {
  const action = values ?? [];
  if (action.length >= 2) {
    return {
      on: (action[0] ?? 0) >= 0.5,
      sp: Math.min(13, Math.max(7, action[1] ?? 10)),
    };
  }
  const index = Math.round(action[0] ?? 0);
  return {
    on: index >= 1,
    sp: Math.min(13, Math.max(7, index + 6)),
  };
};

const PetAvatar: React.FC<{
  active?: boolean;
  bare?: boolean;
  compact?: boolean;
  launcher?: boolean;
  className?: string;
  mood?: PetMood;
  label?: boolean;
}> = ({
  active = false,
  bare = false,
  compact = false,
  launcher = false,
  className = '',
  mood = active ? 'excited' : 'idle',
  label = false,
}) => {
  const sizeClass = launcher
    ? compact
      ? 'h-[76px] w-[76px]'
      : 'h-[112px] w-[112px] max-md:h-[88px] max-md:w-[88px]'
    : compact
      ? 'h-12 w-12'
      : 'h-20 w-20';

  return (
  <div
    className={`aees-pet aees-pet-${mood} ${launcher ? 'aees-pet-launcher' : ''} relative flex-shrink-0 ${sizeClass} ${className}`}
    aria-hidden="true"
  >
    <style>{`
      @keyframes aeesPetFloat { 0%, 100% { transform: translateY(0) rotate(-0.6deg); } 50% { transform: translateY(-3px) rotate(0.8deg); } }
      @keyframes aeesPetIdleRoutine {
        0%, 12%, 100% { transform: translateY(0) rotate(-0.8deg); }
        20% { transform: translateY(-3px) rotate(1.2deg); }
        27% { transform: translateY(-1px) rotate(0.3deg); }
        38%, 55% { transform: translateY(0) rotate(0); }
        64% { transform: translateY(-6px) rotate(-1.4deg); }
        70% { transform: translateY(-2px) rotate(1deg); }
        76% { transform: translateY(0) rotate(-0.4deg); }
        86% { transform: translateY(-2px) rotate(0.7deg); }
      }
      @keyframes aeesPetBreathe { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.012, 1.025); } }
      @keyframes aeesPetGroundShadow { 0%, 100% { transform: translateX(-50%) scaleX(1); opacity: 0.2; } 50% { transform: translateX(-50%) scaleX(0.82); opacity: 0.12; } }
      @keyframes aeesPetIdleSpark {
        0%, 54%, 100% { transform: translateY(3px) scale(0.35) rotate(0); opacity: 0; }
        60% { transform: translateY(0) scale(1) rotate(45deg); opacity: 0.85; }
        70% { transform: translateY(-5px) scale(0.75) rotate(90deg); opacity: 0; }
      }
      @keyframes aeesPetPresence { 0%, 76%, 100% { transform: scale(1); } 82% { transform: scale(1.28); } 88% { transform: scale(0.92); } 94% { transform: scale(1.12); } }
      @keyframes aeesPetListen { 0%, 100% { transform: translateY(0) rotate(-1.5deg); } 50% { transform: translateY(-2px) rotate(1.5deg); } }
      @keyframes aeesPetThinkPose { 0%, 100% { transform: translateY(0) rotate(0); } 50% { transform: translateY(-2px) rotate(-1deg); } }
      @keyframes aeesPetHop { 0%, 100% { transform: translateY(0) scale(1); } 38% { transform: translateY(-7px) scale(1.025); } 68% { transform: translateY(-2px) scale(0.99); } }
      @keyframes aeesPetAlert { 0%, 100% { transform: translateX(0); } 25% { transform: translateX(-1.5px); } 75% { transform: translateX(1.5px); } }
      @keyframes aeesPetSleep { 0%, 100% { transform: translateY(1px) rotate(-0.5deg); } 50% { transform: translateY(3px) rotate(0.5deg); } }
      @keyframes aeesPetThinkBubble { 0%, 100% { transform: translateY(0); opacity: 0.45; } 50% { transform: translateY(-8px); opacity: 1; } }
      @keyframes aeesPetPulse { 0%, 100% { transform: scale(1); opacity: 0.75; } 50% { transform: scale(1.08); opacity: 1; } }
      .aees-pet-stage { animation: aeesPetFloat 4.8s ease-in-out infinite; transform-origin: 50% 72%; }
      .aees-pet-idle.aees-pet-launcher .aees-pet-stage { animation: aeesPetIdleRoutine 6.8s cubic-bezier(0.4, 0, 0.2, 1) infinite; }
      .aees-pet-idle .aees-pet-sprite { animation: aeesPetBreathe 2.8s ease-in-out infinite; transform-origin: 50% 72%; }
      .aees-pet-idle.aees-pet-launcher .aees-pet-ground-shadow { animation: aeesPetGroundShadow 2.8s ease-in-out infinite; }
      .aees-pet-idle.aees-pet-launcher .aees-pet-idle-spark { animation: aeesPetIdleSpark 6.8s ease-in-out infinite; }
      .aees-pet-idle .aees-pet-presence-dot { animation: aeesPetPresence 3.6s ease-in-out infinite; }
      .aees-pet-listening .aees-pet-stage { animation: aeesPetListen 1.4s ease-in-out infinite; }
      .aees-pet-thinking .aees-pet-stage { animation: aeesPetThinkPose 1.8s ease-in-out infinite; }
      .aees-pet-thinking .aees-pet-bubble { animation: aeesPetThinkBubble 1.15s ease-in-out infinite; }
      .aees-pet-excited .aees-pet-stage, .group:hover .aees-pet-stage { animation: aeesPetHop 0.8s ease-in-out infinite; }
      .aees-pet-alert .aees-pet-stage { animation: aeesPetAlert 0.36s ease-in-out 3; }
      .aees-pet-alert .aees-pet-alert-dot, .aees-pet-excited .aees-pet-alert-dot { animation: aeesPetPulse 1s ease-in-out infinite; }
      .aees-pet-sleepy .aees-pet-stage { animation: aeesPetSleep 4.6s ease-in-out infinite; filter: saturate(0.72); opacity: 0.88; }
      @media (prefers-reduced-motion: reduce) {
        .aees-pet-stage, .aees-pet-sprite, .aees-pet-bubble, .aees-pet-alert-dot,
        .aees-pet-ground-shadow, .aees-pet-idle-spark, .aees-pet-presence-dot {
          animation: none !important;
        }
      }
    `}</style>
    {!bare && (
      <span className={`absolute inset-0 rounded-[22px] border shadow-[0_12px_26px_rgba(31,39,45,0.14)] ${
        mood === 'alert'
          ? 'border-[#e1a13a]/45 bg-[#fff7e8]'
          : mood === 'sleepy'
            ? 'border-[#d5dadd] bg-[#f6f8f8]'
            : 'border-[#b8deda] bg-[#effaf9]'
      }`} />
    )}
    {launcher && (
      <span className="aees-pet-ground-shadow pointer-events-none absolute bottom-[5%] left-1/2 h-[7%] w-[43%] -translate-x-1/2 rounded-full bg-[#25343a]/30 blur-[2px]" />
    )}
    <span className="aees-pet-stage absolute inset-0">
      <img
        className="aees-pet-sprite h-full w-full select-none object-contain"
        src={PET_SPRITES[mood]}
        alt=""
        draggable={false}
        style={{
          imageRendering: 'pixelated',
          ...(bare ? { filter: 'drop-shadow(0 12px 18px rgba(31,39,45,0.22))' } : {}),
        }}
      />
    </span>
    {mood === 'idle' && launcher && (
      <>
        <span className="aees-pet-idle-spark pointer-events-none absolute right-[10%] top-[13%] h-2.5 w-2.5 border-2 border-[#0f8b8d] opacity-0" />
        <span
          className="aees-pet-idle-spark pointer-events-none absolute left-[7%] top-[31%] h-2 w-2 border-2 border-[#f2a93b] opacity-0"
          style={{ animationDelay: '0.7s' }}
        />
      </>
    )}
    {mood === 'thinking' && (
      <span className="pointer-events-none absolute right-[5%] top-[3%] h-[34%] w-[30%]">
        <span className="aees-pet-bubble absolute bottom-0 left-0 h-1.5 w-1.5 rounded-full bg-[#0f8b8d]" />
        <span className="aees-pet-bubble absolute left-[38%] top-[28%] h-2.5 w-2.5 rounded-full bg-[#f2a93b]" style={{ animationDelay: '0.18s' }} />
        <span className="aees-pet-bubble absolute right-0 top-0 h-2 w-2 rounded-full bg-[#0f8b8d]" style={{ animationDelay: '0.32s' }} />
      </span>
    )}
    {(mood === 'alert' || mood === 'excited') && (
      <span className={`aees-pet-alert-dot absolute right-[6%] top-[7%] h-3 w-3 rounded-full border-2 border-white ${
        mood === 'alert' ? 'bg-[#e1a13a]' : 'bg-[#28a97f]'
      }`} />
    )}
    <span
      className={`aees-pet-presence-dot absolute -right-0.5 -top-0.5 h-3 w-3 rounded-full border-2 border-white ${
        mood === 'alert' ? 'bg-[#e1a13a]' : active ? 'bg-[#28a97f]' : 'bg-[#aeb7bd]'
      }`}
    />
    {label && (
      <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 rounded-full border border-white bg-[#20272d] px-2 py-0.5 text-[8px] font-semibold text-white shadow-sm">
        {PET_MOOD_LABELS[mood]}
      </span>
    )}
  </div>
  );
};

export const Chatbot: React.FC<ChatbotProps> = ({
  sensorData,
  activeTab = 'overview',
  dataSource = 'simulation',
  observedAt = null,
  onNavigateTab,
  onDataSourceModeChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>(readStoredMessages);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [verbosity, setVerbosity] = useState<'concise' | 'normal' | 'expert'>('concise');
  const [rlCtx, setRlCtx] = useState<RlContext>(initialRlContext);
  const [latestBriefing, setLatestBriefing] = useState(
    '브리핑은 로컬 BEMS 근거로 생성되어 API 토큰을 사용하지 않습니다.',
  );
  const [briefingLoading, setBriefingLoading] = useState(false);
  const [unreadBriefing, setUnreadBriefing] = useState(false);
  const [petReaction, setPetReaction] = useState<PetMood | null>(null);
  const [apiUsage, setApiUsage] = useState<OpenAiUsageSummary | null>(null);
  const [chatCapabilities, setChatCapabilities] = useState<ChatCapabilities | null>(null);
  const [commandGuideOpen, setCommandGuideOpen] = useState(false);
  const [launcherPosition, setLauncherPosition] = useState<LauncherPosition | null>(
    readStoredLauncherPosition,
  );
  const [operationsGuideOpen, setOperationsGuideOpen] = useState(false);
  const [operationsReadiness, setOperationsReadiness] = useState<OperationsReadiness | null>(null);
  const [operationsGuideLoading, setOperationsGuideLoading] = useState(false);
  const [operationsGuideError, setOperationsGuideError] = useState<string | null>(null);
  const [messageFeedback, setMessageFeedback] = useState<Record<number, 'helpful' | 'not_helpful'>>({});
  const [controlProposal, setControlProposal] = useState<ControlAdvisoryProposal | null>(null);
  const [proposalClock, setProposalClock] = useState(Date.now());
  const [proposalBusy, setProposalBusy] = useState(false);
  const [proposalNotice, setProposalNotice] = useState<string | null>(null);
  const [manualControlDraft, setManualControlDraft] = useState<ManualControlDraft | null>(null);
  const [manualControlBusy, setManualControlBusy] = useState(false);
  const [serviceRecovery, setServiceRecovery] = useState<ServiceRecoveryStatus | null>(null);
  const [recoveryBusyService, setRecoveryBusyService] = useState<string | null>(null);
  const [recoveryNotice, setRecoveryNotice] = useState<string | null>(null);

  const scrollRef = useRef<HTMLDivElement>(null);
  const currentContextRef = useRef<Record<string, unknown>>({});
  const currentSnapshotRef = useRef<BriefingSnapshot | null>(null);
  const briefingInFlightRef = useRef(false);
  const isOpenRef = useRef(isOpen);
  const suppressNextLauncherClickRef = useRef(false);
  const operationsGuideAutoHandledRef = useRef(false);
  const lastProposalIdRef = useRef<string | null>(null);
  const lastRecoveryEventIdRef = useRef<string | null>(null);
  const lastRecoveryAttentionKeyRef = useRef<string | null>(null);

  useEffect(() => {
    Object.values(PET_SPRITES).forEach(src => {
      const sprite = new Image();
      sprite.src = src;
    });
  }, []);
  const launcherDragRef = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    originX: number;
    originY: number;
    width: number;
    height: number;
    moved: boolean;
  } | null>(null);

  const viewLabel = activeTab.startsWith('floor-')
    ? `${activeTab.replace('floor-', '').toUpperCase()} Floor Analysis`
    : VIEW_LABELS[activeTab] ?? activeTab;
  const isTwinHome = activeTab === 'overview';
  const isReporting = activeTab === 'reporting';
  const launcherSizePx = 112;
  const recommendedPrompts = dataSource === 'energyplus'
    ? [...ENERGYPLUS_SETPOINT_PROMPTS, ...QUICK_PROMPTS]
    : QUICK_PROMPTS;
  const closeCopilot = useCallback(() => {
    isOpenRef.current = false;
    setIsOpen(false);
  }, []);
  const refreshApiUsage = useCallback(async () => {
    try {
      const response = await fetch('/api/chat/usage', { cache: 'no-store' });
      if (!response.ok) return;
      setApiUsage(await response.json());
    } catch {
      // Usage telemetry must never block the operator UI.
    }
  }, []);
  const refreshChatCapabilities = useCallback(async () => {
    try {
      const response = await fetch('/api/chat/capabilities', { cache: 'no-store' });
      if (!response.ok) throw new Error('capabilities unavailable');
      setChatCapabilities(await response.json() as ChatCapabilities);
    } catch {
      setChatCapabilities(null);
    }
  }, []);
  const refreshOperationsReadiness = useCallback(async () => {
    setOperationsGuideLoading(true);
    setOperationsGuideError(null);
    try {
      const response = await fetch('/api/operations/readiness', {
        cache: 'no-store',
        headers: AuthService.authHeaders(),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || payload.message || '운영 준비 상태를 확인하지 못했습니다.');
      setOperationsReadiness(payload as OperationsReadiness);
    } catch (error) {
      setOperationsGuideError(error instanceof Error ? error.message : '운영 준비 상태를 확인하지 못했습니다.');
    } finally {
      setOperationsGuideLoading(false);
    }
  }, []);
  const refreshServiceRecovery = useCallback(async () => {
    try {
      const response = await fetch('/api/recovery/status', {
        cache: 'no-store',
        headers: AuthService.authHeaders(),
      });
      const payload = await response.json().catch(() => ({})) as ServiceRecoveryStatus;
      if (!response.ok && !payload?.overall) throw new Error(payload?.error || 'Watchdog 상태를 확인하지 못했습니다.');
      setServiceRecovery(payload);
      const event = payload.latestEvent;
      const requiresAttention = ['degraded', 'attention'].includes(payload.overall);
      if (event?.id && lastRecoveryEventIdRef.current !== event.id) {
        const firstObservation = lastRecoveryEventIdRef.current == null;
        lastRecoveryEventIdRef.current = event.id;
        const eventAgeMs = Date.now() - Date.parse(event.timestamp);
        if ((!firstObservation || eventAgeMs <= 120_000) && event.type !== 'watchdog_started') {
          setRecoveryNotice(event.message);
          setUnreadBriefing(true);
          setPetReaction(requiresAttention ? 'alert' : 'excited');
        }
      } else if (requiresAttention) {
        const target = payload.services?.find(service => ['degraded', 'offline', 'attention'].includes(service.status));
        const attentionKey = `${payload.overall}:${target?.id || 'watchdog'}:${target?.status || 'unknown'}`;
        if (lastRecoveryAttentionKeyRef.current !== attentionKey) {
          lastRecoveryAttentionKeyRef.current = attentionKey;
          setRecoveryNotice(target ? `${target.label} 상태를 확인해 주세요.` : 'BEMS 서비스 상태를 확인해 주세요.');
          setUnreadBriefing(true);
          setPetReaction('alert');
        }
      } else {
        lastRecoveryAttentionKeyRef.current = null;
      }
    } catch (error) {
      setServiceRecovery({
        schemaVersion: 'aees-service-watchdog-v1',
        overall: 'attention',
        watchdogConnected: false,
        services: [],
        error: error instanceof Error ? error.message : '독립 Watchdog에 연결할 수 없습니다.',
      });
    }
  }, []);

  const requestSafeRecovery = useCallback(async (serviceId: RecoveryServiceStatus['id']) => {
    if (recoveryBusyService) return;
    setRecoveryBusyService(serviceId);
    setRecoveryNotice('실행 결과를 보존하고 안전 복구를 준비하고 있습니다.');
    setPetReaction('thinking');
    try {
      const response = await fetch(`/api/recovery/services/${serviceId}/recover`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...AuthService.authHeaders() },
        body: JSON.stringify({ preserve: true }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || '안전 복구에 실패했습니다.');
      setRecoveryNotice(payload.event?.message || '서비스가 정상 복구되었습니다.');
      setPetReaction('excited');
      await refreshServiceRecovery();
    } catch (error) {
      setRecoveryNotice(error instanceof Error ? error.message : '안전 복구에 실패했습니다.');
      setPetReaction('alert');
    } finally {
      setRecoveryBusyService(null);
    }
  }, [recoveryBusyService, refreshServiceRecovery]);
  const applySafeDashboardAction = useCallback((action: SafeDashboardAction) => {
    if (action.reporting && typeof window !== 'undefined') {
      window.sessionStorage.setItem(REPORTING_COMMAND_STORAGE_KEY, JSON.stringify(action.reporting));
    }
    onNavigateTab?.(action.tab);
    if (action.reporting && typeof window !== 'undefined') {
      window.setTimeout(() => {
        window.dispatchEvent(new CustomEvent('aees:reporting-command', {
          detail: action.reporting,
        }));
      }, action.tab === activeTab ? 0 : 180);
    }
  }, [activeTab, onNavigateTab]);
  const submitMessageFeedback = useCallback(async (
    index: number,
    message: ChatMessage,
    rating: 'helpful' | 'not_helpful',
  ) => {
    setMessageFeedback(previous => ({ ...previous, [index]: rating }));
    try {
      const response = await fetch('/api/chat/feedback', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...AuthService.authHeaders(),
        },
        body: JSON.stringify({
          rating,
          workspace: viewLabel,
          groundingStatus: message.groundingStatus,
          evidenceSkills: message.evidence?.map(item => item.skill) || [],
        }),
      });
      if (!response.ok) throw new Error('feedback failed');
    } catch {
      setMessageFeedback(previous => {
        const next = { ...previous };
        delete next[index];
        return next;
      });
    }
  }, [viewLabel]);
  const clampLauncherPosition = useCallback((
    x: number,
    y: number,
    width = launcherSizePx,
    height = launcherSizePx,
  ): LauncherPosition => {
    if (typeof window === 'undefined') return { x, y };
    const margin = 8;
    return {
      x: Math.min(Math.max(margin, x), Math.max(margin, window.innerWidth - width - margin)),
      y: Math.min(Math.max(margin, y), Math.max(margin, window.innerHeight - height - margin)),
    };
  }, [launcherSizePx]);
  const hasCollapsedNotice = Boolean(
    recoveryNotice
    || controlProposal
    || proposalNotice
    || (serviceRecovery && ['degraded', 'attention'].includes(serviceRecovery.overall)),
  );
  const launcherPlacementStyle = !isOpen && launcherPosition && !hasCollapsedNotice
    ? {
      left: `${launcherPosition.x}px`,
      top: `${launcherPosition.y}px`,
      right: 'auto',
      bottom: 'auto',
    }
    : undefined;

  const handleLauncherMouseDown = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    if (event.button !== 0) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const drag = {
      pointerId: -1,
      startX: event.clientX,
      startY: event.clientY,
      originX: rect.left,
      originY: rect.top,
      width: rect.width,
      height: rect.height,
      moved: false,
    };
    launcherDragRef.current = drag;

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const dx = moveEvent.clientX - drag.startX;
      const dy = moveEvent.clientY - drag.startY;
      if (Math.abs(dx) + Math.abs(dy) > 4) drag.moved = true;
      if (!drag.moved) return;
      moveEvent.preventDefault();
      setLauncherPosition(clampLauncherPosition(
        drag.originX + dx,
        drag.originY + dy,
        drag.width,
        drag.height,
      ));
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      suppressNextLauncherClickRef.current = drag.moved;
      if (drag.moved) {
        window.setTimeout(() => {
          suppressNextLauncherClickRef.current = false;
        }, 250);
      }
      launcherDragRef.current = null;
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp, { once: true });
  }, [clampLauncherPosition]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, isTyping]);

  useEffect(() => {
    try {
      window.sessionStorage.setItem(chatHistoryStorageKey(), JSON.stringify(messages.slice(-40)));
    } catch {
      // Restricted storage must not block the copilot.
    }
  }, [messages]);

  useEffect(() => {
    void Promise.all([refreshApiUsage(), refreshChatCapabilities()]);
  }, [refreshApiUsage, refreshChatCapabilities]);

  useEffect(() => {
    if (dataSource !== 'energyplus') {
      setControlProposal(null);
      return undefined;
    }
    let cancelled = false;
    const pollProposal = async () => {
      try {
        const response = await fetch('/api/eplus-testbed/status', {
          cache: 'no-store',
          headers: AuthService.authHeaders(),
        });
        if (!response.ok) return;
        const payload = await response.json();
        const proposal = payload?.advisory_proposal as ControlAdvisoryProposal | null | undefined;
        if (cancelled || !proposal?.id) return;
        if (proposal.status === 'pending' && Date.parse(proposal.expires_at) > Date.now()) {
          setControlProposal(proposal);
          setProposalClock(Date.now());
          if (lastProposalIdRef.current !== proposal.id) {
            lastProposalIdRef.current = proposal.id;
            setProposalNotice(null);
            setUnreadBriefing(true);
            setPetReaction('alert');
          }
          return;
        }
        if (lastProposalIdRef.current === proposal.id) {
          setControlProposal(null);
          setProposalBusy(false);
          setProposalNotice(
            proposal.status === 'accepted'
              ? '제안값을 다음 EnergyPlus 스텝에 적용했습니다.'
              : proposal.status === 'rejected'
                ? '제안을 적용하지 않고 기존 설정값을 유지했습니다.'
                : proposal.status === 'expired'
                  ? '응답 시간이 지나 기존 설정값을 유지하고 다음 스텝으로 진행했습니다.'
                  : '운영 모드 변경으로 제안을 취소하고 기존 설정값을 유지했습니다.',
          );
        }
      } catch {
        // A brief testbed restart must not create a false operator alert.
      }
    };
    void pollProposal();
    const timer = window.setInterval(pollProposal, 1_000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [dataSource]);

  useEffect(() => {
    if (!controlProposal) return undefined;
    const timer = window.setInterval(() => setProposalClock(Date.now()), 1_000);
    return () => window.clearInterval(timer);
  }, [controlProposal?.id]);

  useEffect(() => {
    if (!proposalNotice) return undefined;
    const timer = window.setTimeout(() => setProposalNotice(null), 7_000);
    return () => window.clearTimeout(timer);
  }, [proposalNotice]);

  useEffect(() => {
    void refreshOperationsReadiness();
    const handleReadinessChanged = () => void refreshOperationsReadiness();
    window.addEventListener('aees:operations-readiness-changed', handleReadinessChanged);
    return () => window.removeEventListener('aees:operations-readiness-changed', handleReadinessChanged);
  }, [refreshOperationsReadiness]);

  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      if (!cancelled) await refreshServiceRecovery();
    };
    void poll();
    const timer = window.setInterval(() => void poll(), 10_000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [refreshServiceRecovery]);

  useEffect(() => {
    if (!recoveryNotice || serviceRecovery?.overall === 'attention' || serviceRecovery?.overall === 'degraded') return undefined;
    const timer = window.setTimeout(() => setRecoveryNotice(null), 10_000);
    return () => window.clearTimeout(timer);
  }, [recoveryNotice, serviceRecovery?.overall]);

  useEffect(() => {
    if (!operationsReadiness || operationsGuideAutoHandledRef.current) return;
    operationsGuideAutoHandledRef.current = true;
    const user = AuthService.getUser();
    const key = `aees:operations-guide-seen:${OPERATIONS_GUIDE_VERSION}:${user?.username ?? 'user'}:${operationsReadiness.facility.id ?? 'facility'}`;
    try {
      if (window.localStorage.getItem(key) === '1') return;
    } catch {
      // Restricted storage should not prevent first-run guidance.
    }
    setOperationsGuideOpen(true);
    setIsOpen(true);
    isOpenRef.current = true;
    setPetReaction('excited');
  }, [operationsReadiness]);

  useEffect(() => {
    isOpenRef.current = isOpen;
    if (isOpen) setUnreadBriefing(false);
  }, [isOpen]);

  useEffect(() => {
    if (!launcherPosition) return;
    localStorage.setItem('aees:pet-launcher-position', JSON.stringify(launcherPosition));
  }, [launcherPosition]);

  useEffect(() => {
    if (!launcherPosition) return undefined;
    const handleResize = () => {
      setLauncherPosition(position => (position ? clampLauncherPosition(position.x, position.y) : position));
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [clampLauncherPosition, launcherPosition]);

  useEffect(() => {
    if (!petReaction) return undefined;
    const timer = window.setTimeout(
      () => setPetReaction(null),
      PET_REACTION_DURATION_MS[petReaction],
    );
    return () => window.clearTimeout(timer);
  }, [petReaction]);

  useEffect(() => {
    const fetchRlContext = async () => {
      try {
        const [statusRes, actionRes, xaiRes, trackingRes] = await Promise.allSettled([
          fetch('/api/rl/status'),
          fetch('/api/rl/action'),
          fetch('/api/rl/xai/summary'),
          fetch('/api/twin/tracking/status', { cache: 'no-store' }),
        ]);

        const statusOk = statusRes.status === 'fulfilled' && statusRes.value.ok;
        const actionOk = actionRes.status === 'fulfilled' && actionRes.value.ok;
        const xaiOk = xaiRes.status === 'fulfilled' && xaiRes.value.ok;
        const trackingOk = trackingRes.status === 'fulfilled' && trackingRes.value.ok;
        const status = statusOk ? await statusRes.value.json() : null;
        const action = actionOk ? await actionRes.value.json() : null;
        const xai = xaiOk ? await xaiRes.value.json() : null;
        const tracking = trackingOk ? await trackingRes.value.json() : null;
        const actions = action?.actions as Record<string, { values: number[] }> | undefined;
        const chiller = actions?.chiller_agent ? parseChillerAction(actions.chiller_agent.values) : null;
        const trackingSetpoints = tracking?.setpoints ?? {};

        const topFeatures = Object.entries(xai?.agents ?? {})
          .flatMap(([agent, payload]: [string, any]) =>
            (payload?.features ?? []).map((feature: any) => ({
              agent,
              feature: feature.feature,
              contrib: Number(feature.actor_contrib ?? 0),
            })),
          )
          .sort((a, b) => Math.abs(b.contrib) - Math.abs(a.contrib))
          .slice(0, 8);

        setRlCtx({
          connected: statusOk,
          totalSteps: status?.total_steps ?? 0,
          episode: status?.episode ?? 0,
          bufferSize: status?.buffer_size ?? 0,
          latestReward: status?.latest_reward ?? null,
          isTraining: status?.is_training ?? false,
          ahuSaTemp: actions?.ahu_agent?.values?.[0] ?? trackingSetpoints.ahu_sa_temp ?? null,
          chwOn: chiller?.on ?? (trackingSetpoints.chw_sp != null ? Number(trackingSetpoints.chw_sp) < 29 : null),
          chwSp: chiller?.sp ?? trackingSetpoints.chw_sp ?? null,
          cwSp: actions?.ct_agent?.values?.[0] ?? trackingSetpoints.cw_sp ?? null,
          bt4Sp: actions?.bt4_agent?.values?.[0] ?? trackingSetpoints.bt4_sp ?? null,
          bufferTankSp: trackingSetpoints.buffer_tank_sp ?? null,
          topFeatures,
        });
      } catch {
        setRlCtx(previous => ({ ...previous, connected: false }));
      }
    };

    fetchRlContext();
    const timer = window.setInterval(fetchRlContext, 30_000);
    return () => window.clearInterval(timer);
  }, []);

  const snapshot = useMemo<BriefingSnapshot>(() => ({
    dataSource,
    temp: finite(sensorData.temp),
    humidity: finite(sensorData.humidity),
    power: finite(sensorData.power),
    outdoorTemp: finite(sensorData.outdoorTemp),
    co2: finite(sensorData.co2),
    rlConnected: rlCtx.connected,
    ahuSaTemp: rlCtx.ahuSaTemp,
    chwOn: rlCtx.chwOn,
    chwSp: rlCtx.chwSp,
    cwSp: rlCtx.cwSp,
    bt4Sp: rlCtx.bt4Sp,
    bufferTankSp: rlCtx.bufferTankSp,
  }), [dataSource, rlCtx, sensorData]);

  const hasBemsData = Boolean(observedAt) && [
    snapshot.temp,
    snapshot.humidity,
    snapshot.power,
    snapshot.outdoorTemp,
    snapshot.co2,
  ].some(value => Number.isFinite(value) && value !== 0);
  const sensorAlert = hasBemsData && hasOperationalAlert(snapshot);
  const recoveryAttentionService = serviceRecovery?.services?.find(service => (
    ['degraded', 'offline', 'attention', 'recovering'].includes(service.status)
  )) || null;
  const recoveryNeedsAttention = Boolean(serviceRecovery && ['degraded', 'attention'].includes(serviceRecovery.overall));
  const petMood: PetMood = petReaction
    ?? (isTyping || briefingLoading
      ? 'thinking'
      : input.trim()
        ? 'listening'
        : !hasBemsData
          ? 'sleepy'
          : recoveryNeedsAttention || sensorAlert
            ? 'alert'
            : unreadBriefing
              ? 'excited'
              : 'idle');
  const petStatusText = petMood === 'thinking'
    ? 'BEMS 근거 분석 중'
    : petMood === 'listening'
      ? '운영자 질문 입력 중'
      : petMood === 'alert'
        ? recoveryNeedsAttention
          ? 'BEMS 서비스 안전 복구 확인 필요'
          : '운전 임계값 확인 필요'
        : petMood === 'sleepy'
          ? 'BEMS 데이터 또는 분석 서비스 연결 대기'
          : petMood === 'excited'
            ? '새 BEMS 브리핑 도착'
            : rlCtx.connected
              ? 'BEMS·AI 제어 모니터링 중'
              : 'BEMS 모니터링 중 · AI 제어 연결 대기';
  currentSnapshotRef.current = snapshot;
  currentContextRef.current = {
    workspace: viewLabel,
    dataSource,
    timestamp: observedAt,
    generatedFrom: dataSource === 'live'
      ? 'measured live BMS'
      : dataSource === 'historical'
        ? 'historical measured BMS replay'
        : dataSource === 'energyplus'
          ? 'EnergyPlus virtual BEMS with 20-minute MASAC closed-loop control'
        : 'simulation',
    sensor: {
      indoorTempC: sensorData.temp,
      returnHumidityPct: sensorData.humidity,
      comfortProxy: sensorData.pmv,
      hvacDemandKw: sensorData.power,
      outdoorTempC: sensorData.outdoorTemp,
      outdoorHumidityPct: sensorData.outdoorHumidity,
      co2Ppm: sensorData.co2,
    },
    rl: {
      connected: rlCtx.connected,
      training: rlCtx.isTraining,
      episode: rlCtx.episode,
      totalSteps: rlCtx.totalSteps,
      latestReward: rlCtx.latestReward,
      actions: {
        ahuSupplyAirSetpointC: rlCtx.ahuSaTemp,
        chillerOn: rlCtx.chwOn,
        chilledWaterSetpointC: rlCtx.chwSp,
        coolingWaterSetpointC: rlCtx.cwSp,
        bufferTank4SetpointC: rlCtx.bt4Sp,
      },
      topXaiFeatures: rlCtx.topFeatures,
    },
    serviceRecovery: serviceRecovery ? {
      overall: serviceRecovery.overall,
      autoRecoveryEnabled: serviceRecovery.autoRecoveryEnabled,
      services: serviceRecovery.services,
      latestEvent: serviceRecovery.latestEvent || null,
    } : null,
    operationsReport: readStoredOperationsReport(),
  };

  const requestBriefing = useCallback(async () => {
    if (briefingInFlightRef.current) return;
    const context = currentContextRef.current;
    const current = currentSnapshotRef.current;
    if (!current) return;

    briefingInFlightRef.current = true;
    setBriefingLoading(true);
    setPetReaction('thinking');
    try {
      const response = await axios.post('/api/chat/briefing', { context }, { headers: AuthService.authHeaders() as Record<string, string> });
      const text = response.data?.briefing?.trim();
      if (response.data?.usage) setApiUsage(response.data.usage);
      if (text) {
        setLatestBriefing(text);
        setUnreadBriefing(false);
        setPetReaction(hasOperationalAlert(current) ? 'alert' : 'excited');
      }
    } catch (error: any) {
      const message = error?.response?.data?.error;
      const code = error?.response?.data?.code;
      const quotaExceeded = code === 'OPENAI_QUOTA_EXCEEDED' || error?.response?.status === 429;
      if (error?.response?.data?.usage) setApiUsage(error.response.data.usage);
      if (quotaExceeded) {
        setLatestBriefing(message || 'OpenAI API 또는 대시보드 일일 안전 한도에 도달했습니다.');
        setPetReaction('sleepy');
      } else {
        setLatestBriefing(
          message?.includes('not configured')
            ? '로컬 운영 브리핑 서버 설정을 확인해 주세요.'
            : '브리핑 서버 연결을 확인하고 있습니다.',
        );
        setPetReaction('sleepy');
      }
    } finally {
      briefingInFlightRef.current = false;
      setBriefingLoading(false);
      void refreshApiUsage();
    }
  }, [refreshApiUsage]);

  const dismissOperationsGuide = useCallback(() => {
    if (operationsReadiness) {
      const user = AuthService.getUser();
      const key = `aees:operations-guide-seen:${OPERATIONS_GUIDE_VERSION}:${user?.username ?? 'user'}:${operationsReadiness.facility.id ?? 'facility'}`;
      try { window.localStorage.setItem(key, '1'); } catch {}
    }
    setOperationsGuideOpen(false);
  }, [operationsReadiness]);

  const runOperationsGuideAction = useCallback((action: OperationsGuideAction) => {
    if (action === 'status') {
      onNavigateTab?.('overview');
      setMessages(previous => [...previous, { role: 'assistant', content: '공간 트윈으로 이동했어요. 상단 카드에서 현재 실내환경과 설비 상태를 먼저 확인해 주세요.' }]);
      dismissOperationsGuide();
      return;
    }
    if (action === 'new_facility') {
      onNavigateTab?.('settings');
      setMessages(previous => [...previous, { role: 'assistant', content: '환경 설정의 시설 구축 스튜디오로 이동했어요. 준비한 IDF·EPW 또는 BEMS 포인트 자료부터 선택하면 됩니다.' }]);
      dismissOperationsGuide();
      return;
    }
    if (action === 'fine_tuning') {
      if (!operationsReadiness?.calibration_package.ready) {
        setMessages(previous => [...previous, {
          role: 'assistant',
          content: '아직 검증된 보정 결과가 없어 AI 추가 학습을 시작할 수 없어요. 먼저 위의 ‘가상 건물 정확도 확인’을 선택해 자동 보정을 완료해 주세요.',
        }]);
        setPetReaction('alert');
        return;
      }
      onNavigateTab?.('ai-model');
      setMessages(previous => [...previous, {
        role: 'assistant',
        content: operationsReadiness.permissions.can_start_fine_tuning
          ? '정책 튜닝으로 이동했어요. 보정 결과와 학습 조건을 확인한 뒤 격리된 후보 학습을 시작할 수 있습니다. 학습 중에도 현재 운영 정책은 바뀌지 않습니다.'
          : '정책 튜닝으로 이동했어요. 보정 결과는 준비되었지만 학습 시작은 관리자 승인이 필요합니다.',
      }]);
      dismissOperationsGuide();
      return;
    }
    const mode = action === 'calibration' ? 'calibration' : 'demo';
    const request = {
      id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
      mode,
      requestedAt: Date.now(),
    };
    try { window.sessionStorage.setItem(ENERGYPLUS_GUIDE_ACTION_KEY, JSON.stringify(request)); } catch {}
    onDataSourceModeChange?.('energyplus');
    onNavigateTab?.('digital-twin');
    setMessages(previous => [...previous, {
      role: 'assistant',
      content: mode === 'calibration'
        ? '가상 건물 자동 보정을 준비했어요. EnergyPlus가 하루 분량을 비교하고, 완료 후 정확도 기준을 통과하면 보정 결과를 자동 저장합니다. 실제 현장 제어에는 영향을 주지 않습니다.'
        : 'EnergyPlus 안전 시연을 준비했어요. Tracking과 AI 제어 흐름을 보여주지만 실제 현장 설비에는 값을 쓰지 않습니다.',
    }]);
    dismissOperationsGuide();
    setIsOpen(false);
    isOpenRef.current = false;
    window.setTimeout(() => {
      window.dispatchEvent(new CustomEvent('aees:energyplus-guide-action', { detail: request }));
    }, 500);
  }, [dismissOperationsGuide, onDataSourceModeChange, onNavigateTab, operationsReadiness]);

  const handleSend = async () => {
    const userMessage = input.trim();
    if (!userMessage || isTyping) return;
    const uiAction = resolveSafeDashboardAction(userMessage);
    const localIdentityReply = identityReply(userMessage);
    setMessages(previous => [...previous, { role: 'user', content: userMessage }]);
    setInput('');
    setIsTyping(true);
    setPetReaction('thinking');
    if (uiAction) {
      applySafeDashboardAction(uiAction);
    }

    if (localIdentityReply) {
      setMessages(previous => [...previous, { role: 'assistant', content: localIdentityReply }]);
      setIsTyping(false);
      setPetReaction('excited');
      return;
    }

    const requestedManualChange = parseManualControlRequest(userMessage);
    if (requestedManualChange && dataSource === 'energyplus') {
      try {
        const response = await fetch('/api/eplus-testbed/status', {
          cache: 'no-store',
          headers: AuthService.authHeaders(),
        });
        const status = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(status.error || 'EnergyPlus 상태를 확인하지 못했습니다.');
        if (status.operating_mode !== 'manual') {
          throw new Error('현재 운영 모드가 수동제어가 아닙니다. 상단에서 수동을 선택한 뒤 다시 요청해 주세요.');
        }
        if (status.control?.system_on !== true) {
          throw new Error('현재 HVAC가 OFF여서 설정온도 명령을 적용하지 않습니다. 시스템 ON 구간에서 다시 요청해 주세요.');
        }
        const current = status.control?.actions as ManualControlDraft | undefined;
        if (!current) throw new Error('현재 적용값을 아직 확인하지 못했습니다. 첫 EnergyPlus 스텝 이후 다시 요청해 주세요.');
        const draft = { ...current, ...requestedManualChange };
        const validationError = validateManualControlDraft(draft);
        if (validationError) throw new Error(validationError);
        setManualControlDraft(draft);
        setMessages(previous => [...previous, {
          role: 'assistant',
          content: `수동 변경 요청을 확인했어요. 아래 값이 맞는지 확인한 뒤 적용 버튼을 눌러 주세요.\n급기 ${draft.ahu_supply_air_setpoint_c.toFixed(1)}°C · 냉수 ${draft.chilled_water_setpoint_c.toFixed(1)}°C · 냉각수 ${draft.condenser_water_setpoint_c.toFixed(1)}°C · BT4 ${draft.buffer_tank_4_setpoint_c.toFixed(1)}°C`,
        }]);
        setPetReaction('alert');
      } catch (error) {
        setMessages(previous => [...previous, {
          role: 'assistant',
          content: error instanceof Error ? error.message : '수동 변경 요청을 확인하지 못했습니다.',
        }]);
        setPetReaction('alert');
      } finally {
        setIsTyping(false);
      }
      return;
    }

    try {
      const response = await axios.post('/api/chat', {
        message: userMessage,
        context: {
          ...currentContextRef.current,
          responseStyle: verbosity,
        },
      }, { headers: AuthService.authHeaders() as Record<string, string> });
      if (response.data?.usage) setApiUsage(response.data.usage);
      setMessages(previous => [
        ...previous,
        {
          role: 'assistant',
          content: `${uiAction ? `${uiAction.reply}\n\n` : ''}${response.data.response || '설명 결과를 생성하지 못했습니다.'}`,
          evidence: Array.isArray(response.data.evidence) ? response.data.evidence : [],
          groundingStatus: response.data.grounding?.status,
        },
      ]);
      setPetReaction(sensorAlert ? 'alert' : 'idle');
    } catch {
      setMessages(previous => [
        ...previous,
        {
          role: 'assistant',
          content: uiAction
            ? `${uiAction.reply}\n\nOpenAI API 또는 대시보드 서버 연결이 아직 준비되지 않아 BEMS 근거 설명은 생성하지 못했습니다.`
            : 'OpenAI API 또는 대시보드 서버 연결을 확인해 주세요.',
        },
      ]);
      setPetReaction('sleepy');
    } finally {
      setIsTyping(false);
      void refreshApiUsage();
    }
  };

  const decideControlProposal = async (decision: 'accept' | 'reject') => {
    if (!controlProposal || proposalBusy) return;
    setProposalBusy(true);
    try {
      const response = await fetch('/api/eplus-testbed/session/advisory-decision', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...AuthService.authHeaders() },
        body: JSON.stringify({
          proposal_id: controlProposal.id,
          decision,
          actor: AuthService.getUser()?.username || 'dashboard-operator',
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || '제안 응답을 처리하지 못했습니다.');
      setProposalNotice(decision === 'accept' ? '적용 요청을 확인했습니다.' : '기존값 유지 요청을 확인했습니다.');
    } catch (error) {
      setProposalBusy(false);
      setProposalNotice(error instanceof Error ? error.message : '제안 응답을 처리하지 못했습니다.');
    }
  };

  const applyManualControlDraft = async () => {
    if (!manualControlDraft || manualControlBusy) return;
    setManualControlBusy(true);
    try {
      const response = await fetch('/api/eplus-testbed/session/manual-setpoints', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...AuthService.authHeaders() },
        body: JSON.stringify(manualControlDraft),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || '수동 설정값을 전달하지 못했습니다.');
      setMessages(previous => [...previous, {
        role: 'assistant',
        content: '확인한 수동 설정값을 다음 EnergyPlus 20분 스텝에 적용하도록 예약했습니다. 적용 결과는 상단 현재 제어값과 AI 제어 분석 이력에서 확인할 수 있어요.',
      }]);
      setManualControlDraft(null);
      setPetReaction('excited');
    } catch (error) {
      setMessages(previous => [...previous, {
        role: 'assistant',
        content: error instanceof Error ? error.message : '수동 설정값을 적용하지 못했습니다.',
      }]);
      setPetReaction('alert');
    } finally {
      setManualControlBusy(false);
    }
  };

  const proposalRemainingSeconds = controlProposal
    ? Math.max(0, Math.ceil((Date.parse(controlProposal.expires_at) - proposalClock) / 1_000))
    : 0;
  const proposalActionSummary = controlProposal
    ? `급기 ${controlProposal.actions.ahu_supply_air_setpoint_c.toFixed(1)}°C · 냉수 ${controlProposal.actions.chilled_water_setpoint_c.toFixed(1)}°C · 냉각수 ${controlProposal.actions.condenser_water_setpoint_c.toFixed(1)}°C · BT4 ${controlProposal.actions.buffer_tank_4_setpoint_c.toFixed(1)}°C`
    : '';

  return (
    <div className={`fixed right-4 z-[200] flex flex-col items-end max-md:right-3 ${
      dataSource === 'energyplus'
        ? isReporting
          ? 'bottom-[6.25rem] max-md:bottom-[7.5rem]'
          : 'bottom-[7.5rem] max-md:bottom-[8.75rem]'
        : isTwinHome
          ? 'bottom-[4.75rem] max-md:bottom-[8.75rem]'
          : 'bottom-4 max-md:bottom-[4.75rem]'
    }`} style={launcherPlacementStyle}>
      {!isOpen && !controlProposal && (recoveryNotice || recoveryNeedsAttention) && (
        <div className="mb-2 w-[min(390px,calc(100vw-24px))] rounded-2xl border border-[#e4c68d] bg-white p-3 shadow-[0_16px_42px_rgba(31,39,45,0.18)]" role="alert" aria-live="polite">
          <div className="flex items-start gap-2.5">
            <PetAvatar active compact mood={recoveryNeedsAttention ? 'alert' : 'excited'} />
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="min-w-0 break-keep text-xs font-semibold text-[#20272d]">범비의 서버 상태 안내</p>
                <span className={`shrink-0 rounded-full px-2 py-0.5 text-[9px] font-semibold ${
                  serviceRecovery?.overall === 'healthy'
                    ? 'bg-[#eef9f7] text-[#24786a]'
                    : 'bg-[#fff4de] text-[#946012]'
                }`}>
                  {serviceRecovery?.overall === 'recovering' ? '복구 중' : recoveryNeedsAttention ? '확인 필요' : '복구 완료'}
                </span>
              </div>
              <p className="mt-1 break-words text-[11px] leading-4 text-[#59636b]">
                {recoveryNotice || `${recoveryAttentionService?.label || 'BEMS 서비스'} 상태를 확인해 주세요.`}
              </p>
              {recoveryAttentionService && AuthService.getUser()?.role === 'admin' && (
                <button
                  type="button"
                  disabled={Boolean(recoveryBusyService) || serviceRecovery?.overall === 'recovering'}
                  onClick={() => void requestSafeRecovery(recoveryAttentionService.id)}
                  className="mt-2 h-9 w-full rounded-xl bg-[#0f8b8d] text-[11px] font-semibold text-white disabled:opacity-45"
                >
                  <i className={`fa-solid ${recoveryBusyService ? 'fa-circle-notch fa-spin' : 'fa-shield-halved'} mr-1.5`} />
                  실행 결과 보존 후 안전 복구
                </button>
              )}
              <p className="mt-1.5 text-[9px] text-[#8a939a]">독립 Watchdog · 로컬 규칙 기반 · OpenAI API 토큰 0</p>
            </div>
          </div>
        </div>
      )}
      {!isOpen && (controlProposal || proposalNotice) && (
        <div className="mb-2 w-[min(390px,calc(100vw-24px))] rounded-2xl border border-[#d7dbde] bg-white p-3 shadow-[0_16px_42px_rgba(31,39,45,0.18)]" role="status" aria-live="polite">
          <div className="flex items-start gap-2.5">
            <PetAvatar active compact mood={controlProposal ? 'alert' : 'excited'} />
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="min-w-0 break-keep text-xs font-semibold text-[#20272d]">{controlProposal ? '범비의 AI 제어 제안' : '범비 알림'}</p>
                {controlProposal && (
                  <span className="shrink-0 rounded-full bg-[#fff4de] px-2 py-0.5 font-mono text-[10px] font-bold text-[#946012]">
                    {Math.floor(proposalRemainingSeconds / 60)}:{String(proposalRemainingSeconds % 60).padStart(2, '0')}
                  </span>
                )}
              </div>
              <p className="mt-1 break-words text-[11px] leading-4 text-[#59636b]">
                {controlProposal ? proposalActionSummary : proposalNotice}
              </p>
              {controlProposal && (
                <>
                  <p className="mt-1 text-[10px] leading-4 text-[#7a848c]">시간 내 선택하지 않으면 현재 설정값을 유지하고 다음 스텝으로 넘어갑니다.</p>
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      disabled={proposalBusy || proposalRemainingSeconds <= 0}
                      onClick={() => void decideControlProposal('accept')}
                      className="h-9 rounded-xl bg-[#0f8b8d] text-[11px] font-semibold text-white disabled:opacity-45"
                    >
                      <i className={`fa-solid ${proposalBusy ? 'fa-circle-notch fa-spin' : 'fa-check'} mr-1.5`} />제안 적용
                    </button>
                    <button
                      type="button"
                      disabled={proposalBusy || proposalRemainingSeconds <= 0}
                      onClick={() => void decideControlProposal('reject')}
                      className="h-9 rounded-xl border border-[#d7dbde] bg-white text-[11px] font-semibold text-[#59636b] disabled:opacity-45"
                    >
                      <i className="fa-solid fa-arrow-rotate-left mr-1.5" />기존값 유지
                    </button>
                  </div>
                </>
              )}
              <p className="mt-1.5 text-[9px] text-[#8a939a]">로컬 제어 알림 · OpenAI API 토큰 0</p>
            </div>
          </div>
        </div>
      )}
      {isOpen && (
        <section className={`mb-3 flex w-[min(460px,calc(100vw-24px))] flex-col overflow-hidden rounded-[22px] border border-[#d5dadd] bg-[#fbfcfc]/95 shadow-[0_22px_58px_rgba(31,39,45,0.18)] backdrop-blur-xl ${
          dataSource === 'energyplus'
            ? 'h-[min(680px,calc(100vh-9rem))] max-md:h-[calc(100vh-10rem)]'
            : 'h-[min(680px,calc(100vh-5rem))] max-md:h-[calc(100vh-8rem)]'
        }`}>
          <header className="border-b border-[#e0e4e6] bg-white px-4 py-3">
            <div className="flex items-center justify-between gap-3">
              <div className="flex min-w-0 items-center gap-3">
                <PetAvatar active={rlCtx.connected || briefingLoading} compact mood={petMood} />
                <div className="min-w-0">
                  <h2 className="text-sm font-semibold text-[#20272d]">{COPILOT_NAME} · AI 운영 도우미</h2>
                  <p className="mt-0.5 truncate text-[10px] text-[#77818a]">{viewLabel} · {petStatusText}</p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => {
                    const initial = [{ role: 'assistant' as const, content: COPILOT_INTRODUCTION }];
                    setMessages(initial);
                    try { window.sessionStorage.removeItem(chatHistoryStorageKey()); } catch {}
                  }}
                  className="h-8 w-8 rounded-md text-[#7a848c] hover:bg-black/5 hover:text-[#20272d]"
                  aria-label="대화 기록 지우기"
                  title="현재 세션 대화 지우기"
                >
                  <i className="fa-solid fa-trash-can text-[11px]" />
                </button>
                <button
                  type="button"
                  onPointerDown={event => {
                    event.preventDefault();
                    event.stopPropagation();
                    closeCopilot();
                  }}
                  onClick={closeCopilot}
                  className="h-8 w-8 rounded-md text-[#7a848c] hover:bg-black/5 hover:text-[#20272d]"
                  aria-label="Close copilot"
                >
                  <i className="fa-solid fa-xmark" />
                </button>
              </div>
            </div>

            <div className="mt-3 flex items-center gap-2">
              <button
                type="button"
                onClick={() => {
                  setOperationsGuideOpen(open => !open);
                  if (!operationsReadiness) void refreshOperationsReadiness();
                }}
                className={`h-8 rounded-full border px-3 text-[10px] font-semibold ${
                  operationsGuideOpen
                    ? 'border-[#79bbb5] bg-[#eef9f7] text-[#08777d]'
                    : 'border-[#d7dbde] bg-white text-[#4f5962] hover:border-[#79bbb5] hover:text-[#08777d]'
                }`}
              >
                <i className="fa-solid fa-route mr-1" />운영 준비
              </button>
              <button
                type="button"
                onClick={() => requestBriefing()}
                disabled={briefingLoading}
                className="h-8 rounded-full border border-[#d7dbde] bg-white px-3 text-[10px] font-semibold text-[#4f5962] hover:border-[#79bbb5] hover:text-[#08777d] disabled:opacity-40"
              >
                <i className={`fa-solid ${briefingLoading ? 'fa-circle-notch fa-spin' : 'fa-rotate'} mr-1`} />
                브리핑
              </button>
              <select
                value={verbosity}
                onChange={event => setVerbosity(event.target.value as typeof verbosity)}
                className="ml-auto h-8 rounded-full border border-[#d7dbde] bg-white px-3 text-[10px] font-semibold text-[#66717a] outline-none"
                aria-label="Response detail"
              >
                <option value="concise">간단히</option>
                <option value="normal">보통</option>
                <option value="expert">상세</option>
              </select>
            </div>
            <button
              type="button"
              onClick={() => setCommandGuideOpen(open => !open)}
              aria-expanded={commandGuideOpen}
              className="mt-2 flex w-full items-center gap-2 rounded-xl border border-[#bcded7] bg-[#f0faf8] px-3 py-2 text-left transition hover:border-[#79bbb5] hover:bg-[#e9f7f5]"
            >
              <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-[#24786a]">
                <i className="fa-solid fa-circle-check text-[9px]" />로컬 명령 사용 가능
              </span>
              <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[9px] font-semibold ${
                chatCapabilities?.configured
                  ? 'bg-white text-[#08777d]'
                  : chatCapabilities
                    ? 'bg-[#fff4de] text-[#946012]'
                    : 'bg-white text-[#77818a]'
              }`}>
                <i className={`fa-solid ${chatCapabilities?.configured ? 'fa-link' : chatCapabilities ? 'fa-link-slash' : 'fa-circle-notch fa-spin'}`} />
                {chatCapabilities?.configured ? 'OpenAI API 키 설정됨' : chatCapabilities ? 'OpenAI API 키 없음' : 'API 설정 확인 중'}
              </span>
              <span className="ml-auto text-[9px] font-semibold text-[#5f6b72]">
                명령 안내 <i className={`fa-solid ${commandGuideOpen ? 'fa-chevron-up' : 'fa-chevron-down'} ml-1`} />
              </span>
            </button>
            <p className="mt-2 max-h-8 overflow-hidden text-[10px] leading-4 text-[#66717a]">
              {latestBriefing}
            </p>
            {apiUsage && (
              <div className="mt-2 flex items-center justify-between gap-3 rounded-lg border border-[#e1e5e7] bg-[#f7f8f8] px-2.5 py-1.5 text-[9px] font-mono text-[#69747c]">
                <span>오늘 {apiUsage.requestCount}/{apiUsage.limits.requests}회</span>
                <span>{apiUsage.totalTokens.toLocaleString()}/{apiUsage.limits.tokens.toLocaleString()} tokens</span>
              </div>
            )}
          </header>

          <div ref={scrollRef} className="custom-scrollbar flex-1 space-y-4 overflow-y-auto bg-[#f7f8f8] p-4">
            {commandGuideOpen && (
              <section className="rounded-2xl border border-[#bcded7] bg-white p-3 shadow-[0_8px_22px_rgba(31,72,72,0.07)]" aria-label="범비 명령 사용 안내">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-xs font-semibold text-[#20272d]">범비에게 이렇게 요청할 수 있어요</p>
                    <p className="mt-1 text-[10px] leading-4 text-[#66717a]">항목을 누르면 입력창에 예시 명령이 들어갑니다. 전송 전 내용을 확인할 수 있어요.</p>
                  </div>
                  <span className="shrink-0 rounded-full bg-[#eaf7f4] px-2 py-1 text-[9px] font-semibold text-[#24786a]">토큰 0</span>
                </div>

                <div className="mt-3 rounded-xl bg-[#f2f8f7] p-2.5">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-[10px] font-semibold text-[#24786a]">API 없이 사용 가능</p>
                    <span className="text-[9px] text-[#71807f]">로컬 BEMS 근거 조회·화면 이동</span>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {LOCAL_COMMAND_EXAMPLES.map(command => (
                      <button
                        key={command.label}
                        type="button"
                        onClick={() => {
                          setInput(command.prompt);
                          setPetReaction('listening');
                        }}
                        className="rounded-full border border-[#bcded7] bg-white px-2.5 py-1.5 text-[9px] font-semibold text-[#316d68] transition hover:border-[#79bbb5] hover:bg-[#e9f7f5]"
                        title={command.prompt}
                      >
                        {command.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className={`mt-2 rounded-xl border p-2.5 ${
                  chatCapabilities?.configured
                    ? 'border-[#c9dedb] bg-[#fbfdfd]'
                    : 'border-[#ead8b5] bg-[#fffaf0]'
                }`}>
                  <div className="flex items-center justify-between gap-2">
                    <p className={`text-[10px] font-semibold ${chatCapabilities?.configured ? 'text-[#08777d]' : 'text-[#946012]'}`}>OpenAI API가 필요한 기능</p>
                    <span className="text-[9px] text-[#71807f]">
                      {chatCapabilities?.configured ? '현재 사용 가능 · 호출 시 토큰 사용' : '현재 사용 불가 · API 연결 필요'}
                    </span>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {OPENAI_COMMAND_EXAMPLES.map(command => (
                      <button
                        key={command.label}
                        type="button"
                        disabled={!chatCapabilities?.configured}
                        onClick={() => {
                          setInput(command.prompt);
                          setPetReaction('listening');
                        }}
                        className="rounded-full border border-[#c9dedb] bg-white px-2.5 py-1.5 text-[9px] font-semibold text-[#3f6664] transition hover:border-[#79bbb5] hover:bg-[#eef9f7] disabled:cursor-not-allowed disabled:border-[#e0dfdb] disabled:bg-[#f2f2f0] disabled:text-[#9a9d9d]"
                        title={chatCapabilities?.configured ? command.prompt : 'OpenAI API 키 설정 후 사용할 수 있습니다.'}
                      >
                        {command.label}
                      </button>
                    ))}
                  </div>
                </div>

                <p className="mt-2 rounded-lg border border-[#e1e5e7] bg-[#f7f8f8] px-2.5 py-2 text-[9px] leading-4 text-[#66717a]">
                  <i className="fa-solid fa-shield-halved mr-1 text-[#08777d]" />API 연결 여부와 관계없이 현장 설비의 직접 제어는 제한됩니다. 수동 설정·제안 적용·서버 복구는 권한과 최종 확인 절차를 거칩니다.
                </p>
              </section>
            )}
            {serviceRecovery && serviceRecovery.overall !== 'healthy' && (
              <div className={`rounded-2xl border p-3 ${
                serviceRecovery.overall === 'recovering'
                  ? 'border-[#bcded7] bg-[#f0faf8] text-[#245f58]'
                  : 'border-[#e4c68d] bg-[#fff9ed] text-[#6f511b]'
              }`} role={recoveryNeedsAttention ? 'alert' : 'status'}>
                <div className="flex items-center justify-between gap-2">
                  <p className="text-xs font-semibold"><i className="fa-solid fa-server mr-1.5" />BEMS 서비스 복구</p>
                  <span className="rounded-full bg-white px-2 py-0.5 text-[9px] font-semibold">
                    {serviceRecovery.overall === 'recovering' ? '복구 중' : '운영자 확인 필요'}
                  </span>
                </div>
                <p className="mt-2 text-[11px] leading-4">
                  {recoveryNotice || serviceRecovery.error || `${recoveryAttentionService?.label || '독립 Watchdog'} 상태를 확인해 주세요.`}
                </p>
                {recoveryAttentionService && (
                  <p className="mt-1 text-[10px] leading-4 opacity-80">
                    {recoveryAttentionService.lastKnownActive
                      ? '실행 중인 결과를 먼저 보존한 뒤 해당 서비스만 재시작합니다.'
                      : '일시 오류를 3회 확인한 후 해당 서비스만 제한적으로 복구합니다.'}
                  </p>
                )}
                {recoveryAttentionService && AuthService.getUser()?.role === 'admin' ? (
                  <button
                    type="button"
                    disabled={Boolean(recoveryBusyService) || serviceRecovery.overall === 'recovering'}
                    onClick={() => void requestSafeRecovery(recoveryAttentionService.id)}
                    className="mt-2 h-9 w-full rounded-xl bg-[#0f8b8d] text-[11px] font-semibold text-white disabled:opacity-45"
                  >
                    <i className={`fa-solid ${recoveryBusyService ? 'fa-circle-notch fa-spin' : 'fa-shield-halved'} mr-1.5`} />
                    안전 복구
                  </button>
                ) : recoveryAttentionService ? (
                  <p className="mt-2 rounded-lg bg-white/70 px-2.5 py-2 text-[10px] font-semibold">관리자 승인 후 안전 복구할 수 있습니다.</p>
                ) : null}
                <p className="mt-1.5 text-[9px] opacity-70">현장 쓰기 차단·모니터링 안전 강등·감사 로그 · OpenAI API 토큰 0</p>
              </div>
            )}
            {manualControlDraft && (
              <div className="rounded-2xl border border-[#bcded7] bg-[#f0faf8] p-3 text-[#245f58]">
                <p className="text-xs font-semibold"><i className="fa-solid fa-hand-pointer mr-1.5" />수동 설정값 최종 확인</p>
                <p className="mt-2 text-[11px] leading-4">급기 {manualControlDraft.ahu_supply_air_setpoint_c.toFixed(1)}°C · 냉수 {manualControlDraft.chilled_water_setpoint_c.toFixed(1)}°C · 냉각수 {manualControlDraft.condenser_water_setpoint_c.toFixed(1)}°C · BT4 {manualControlDraft.buffer_tank_4_setpoint_c.toFixed(1)}°C</p>
                <p className="mt-1 text-[10px]">정형 명령과 확인 버튼으로 처리 · OpenAI API 토큰 0</p>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  <button type="button" disabled={manualControlBusy} onClick={() => void applyManualControlDraft()} className="h-9 rounded-xl bg-[#0f8b8d] text-[11px] font-semibold text-white disabled:opacity-45">{manualControlBusy ? '적용 중' : '다음 스텝에 적용'}</button>
                  <button type="button" disabled={manualControlBusy} onClick={() => setManualControlDraft(null)} className="h-9 rounded-xl border border-[#bcded7] bg-white text-[11px] font-semibold disabled:opacity-45">취소</button>
                </div>
              </div>
            )}
            {controlProposal && (
              <div className="rounded-2xl border border-[#e4c68d] bg-[#fff9ed] p-3 text-[#6f511b]" role="status">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-xs font-semibold"><i className="fa-solid fa-lightbulb mr-1.5" />AI 제어 제안</p>
                  <span className="rounded-full bg-white px-2 py-0.5 font-mono text-[10px] font-bold">{Math.floor(proposalRemainingSeconds / 60)}:{String(proposalRemainingSeconds % 60).padStart(2, '0')}</span>
                </div>
                <p className="mt-2 text-[11px] leading-4">{proposalActionSummary}</p>
                <p className="mt-1 text-[10px]">미응답 시 기존값 유지 · OpenAI API 토큰 0</p>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  <button type="button" disabled={proposalBusy || proposalRemainingSeconds <= 0} onClick={() => void decideControlProposal('accept')} className="h-9 rounded-xl bg-[#0f8b8d] text-[11px] font-semibold text-white disabled:opacity-45">제안 적용</button>
                  <button type="button" disabled={proposalBusy || proposalRemainingSeconds <= 0} onClick={() => void decideControlProposal('reject')} className="h-9 rounded-xl border border-[#e4c68d] bg-white text-[11px] font-semibold disabled:opacity-45">기존값 유지</button>
                </div>
              </div>
            )}
            {!controlProposal && proposalNotice && (
              <div className="rounded-xl border border-[#bcded7] bg-[#f0faf8] px-3 py-2 text-[11px] text-[#24786a]">{proposalNotice}</div>
            )}
            {operationsGuideOpen && (
              <OperationsGuide
                readiness={operationsReadiness}
                loading={operationsGuideLoading}
                error={operationsGuideError}
                onRefresh={() => void refreshOperationsReadiness()}
                onAction={runOperationsGuideAction}
                onDismiss={dismissOperationsGuide}
              />
            )}
            {messages.map((message, index) => (
              <div key={`${message.role}-${index}`} className={`flex items-end gap-2 ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                {message.role === 'assistant' && (
                  <PetAvatar active={rlCtx.connected || briefingLoading} compact mood={petMood} className="mb-1" />
                )}
                <div className={`max-w-[88%] whitespace-pre-line rounded-lg px-3.5 py-3 text-xs leading-relaxed ${
                  message.role === 'user'
                    ? 'rounded-br-sm border border-[#08777d] bg-[#0f8b8d] text-white shadow-[0_6px_16px_rgba(15,139,141,0.16)]'
                    : 'rounded-bl-sm border border-[#d7dbde] bg-white text-[#30383e]'
                }`}>
                  <p className={message.role === 'user' ? 'text-white' : undefined}>{message.content}</p>
                  {message.role === 'assistant' && message.evidence && message.evidence.length > 0 && (
                    <div className="mt-3 border-t border-[#e4e7e8] pt-2">
                      <div className="mb-1.5 flex items-center justify-between gap-2 text-[8px] font-semibold text-[#607078]">
                        <span>응답 근거 {message.evidence.length}건</span>
                        <span>{EVIDENCE_STATUS_LABELS[message.groundingStatus || ''] || '근거 조회'}</span>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {message.evidence.map((item, evidenceIndex) => (
                          <span
                            key={`${item.skill}-${evidenceIndex}`}
                            title={`${item.source} · ${formatEvidenceTime(item.observedAt || item.retrievedAt)}${item.warnings?.length ? ` · ${item.warnings.join(' ')}` : ''}`}
                            className={`rounded-full border px-2 py-1 text-[8px] leading-none ${
                              ['degraded', 'stale', 'unavailable', 'unverified', 'configured_only', 'no_action'].includes(item.status)
                                ? 'border-[#e4c68d] bg-[#fff9ed] text-[#8a641f]'
                                : 'border-[#bcded7] bg-[#f0faf8] text-[#24786a]'
                            }`}
                          >
                            {item.label} · {EVIDENCE_STATUS_LABELS[item.status] || item.status} · {formatEvidenceTime(item.observedAt || item.retrievedAt)}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {message.role === 'assistant' && message.evidence && message.evidence.length > 0 && (
                    <div className="mt-2 flex items-center justify-end gap-1 border-t border-[#eef0f1] pt-2 text-[8px] text-[#8a939a]">
                      <span className="mr-1">도움이 됐나요?</span>
                      {(['helpful', 'not_helpful'] as const).map(rating => (
                        <button
                          key={rating}
                          type="button"
                          onClick={() => void submitMessageFeedback(index, message, rating)}
                          disabled={messageFeedback[index] != null}
                          className={`grid h-6 w-6 place-items-center rounded-full border transition-colors ${
                            messageFeedback[index] === rating
                              ? 'border-[#79bbb5] bg-[#eef9f7] text-[#08777d]'
                              : 'border-[#dfe3e6] bg-white text-[#7a848c] hover:border-[#79bbb5] hover:text-[#08777d] disabled:cursor-default disabled:opacity-45'
                          }`}
                          aria-label={rating === 'helpful' ? '도움이 됨' : '개선 필요'}
                          title={rating === 'helpful' ? '도움이 됨' : '개선 필요'}
                        >
                          <i className={`fa-regular ${rating === 'helpful' ? 'fa-thumbs-up' : 'fa-thumbs-down'}`} />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {isTyping && (
              <div className="flex items-center gap-2 text-[10px] font-semibold text-[#08777d]">
                <PetAvatar active compact mood="thinking" />
                <span>시스템 근거를 확인하고 있습니다...</span>
              </div>
            )}
          </div>

          <footer className="space-y-2.5 border-t border-[#d7dbde] bg-white p-3">
            <div className="flex items-center justify-between gap-3 px-0.5">
              <p className="text-[9px] font-semibold text-[#66717a]">
                {dataSource === 'energyplus' ? '추천 명령 · 수동 설정' : '추천 명령'}
              </p>
              {dataSource === 'energyplus' && (
                <p className="text-right text-[9px] text-[#8a939a]">
                  수동제어 · 시스템 ON에서 사용
                </p>
              )}
            </div>
            <div className="custom-scrollbar flex gap-2 overflow-x-auto pb-0.5">
              {recommendedPrompts.map(command => (
                <button
                  key={`${command.kind}-${command.label}`}
                  type="button"
                  onClick={() => {
                    setInput(command.prompt);
                    setPetReaction('listening');
                  }}
                  className={`shrink-0 rounded-full border px-3 py-1.5 text-[10px] transition-colors ${
                    command.kind === 'setpoint'
                      ? 'border-[#8cc9c3] bg-[#eef9f7] font-semibold text-[#08777d] hover:bg-[#dff3f0]'
                      : 'border-[#d5dadd] bg-[#fbfcfc] text-[#66717a] hover:border-[#79bbb5] hover:bg-[#eef9f7] hover:text-[#08777d]'
                  }`}
                  title={command.kind === 'setpoint'
                    ? `${command.prompt} · EnergyPlus 수동제어·시스템 ON에서 사용`
                    : command.prompt}
                >
                  {command.kind === 'setpoint' && (
                    <i className="fa-solid fa-sliders mr-1.5 text-[8px]" />
                  )}
                  {command.label}
                </button>
              ))}
            </div>
            <div className="relative flex items-center">
              <textarea
                value={input}
                onChange={event => {
                  setInput(event.target.value);
                  setPetReaction(event.target.value.trim() ? 'listening' : null);
                }}
                onKeyDown={event => {
                  if (event.key === 'Enter' && !event.shiftKey) {
                    event.preventDefault();
                    void handleSend();
                  }
                }}
                placeholder={chatCapabilities?.configured
                  ? '운전 상태·그래프·AI 제어 근거 또는 자유 질문을 입력하세요'
                  : '예: 현재 상태 요약, Tracking 상태, 운영 리포트'}
                rows={2}
                className="block max-h-24 min-h-[56px] w-full resize-none rounded-2xl border border-[#b8d2cf] bg-white py-2.5 pl-3 pr-14 text-xs text-[#20272d] outline-none transition focus:border-[#0f8b8d] focus:ring-2 focus:ring-[#0f8b8d]/15"
              />
              <button
                type="button"
                onClick={handleSend}
                disabled={!input.trim() || isTyping}
                className={`absolute inset-y-0 right-2 my-auto grid h-9 w-9 place-items-center rounded-xl border transition ${
                  input.trim() && !isTyping
                    ? 'border-[#0f8b8d] bg-[#0f8b8d] text-white shadow-[0_5px_14px_rgba(15,139,141,0.24)] hover:border-[#08777d] hover:bg-[#08777d]'
                    : 'cursor-not-allowed border-[#d4e2e0] bg-[#eaf2f1] text-[#9ab2af]'
                }`}
                aria-label="메시지 보내기"
                title={input.trim() ? '메시지 보내기' : '명령이나 질문을 입력해 주세요'}
              >
                <i className="fa-solid fa-arrow-up text-xs" />
              </button>
            </div>
          </footer>
        </section>
      )}

      {!isOpen && (
      <button
        type="button"
        onMouseDown={handleLauncherMouseDown}
        onClick={event => {
          if (suppressNextLauncherClickRef.current) {
            suppressNextLauncherClickRef.current = false;
            event.preventDefault();
            return;
          }
          setPetReaction(unreadBriefing ? 'excited' : 'listening');
          setIsOpen(open => !open);
        }}
        className="group relative grid h-[112px] w-[112px] touch-none cursor-grab place-items-center rounded-full bg-transparent p-0 transition-transform hover:scale-[1.02] active:cursor-grabbing focus:outline-none focus:ring-4 focus:ring-[#0f8b8d]/25 max-md:h-[88px] max-md:w-[88px]"
        data-launcher-size="standard"
        aria-label="AI 호랑이 도우미 열기"
      >
        {unreadBriefing && (
          <span className="absolute right-1 top-1 h-3.5 w-3.5 rounded-full border-2 border-white bg-[#f2a93b]" />
        )}
        <PetAvatar
          active={rlCtx.connected || briefingLoading}
          bare
          launcher
          mood={petMood}
        />
        <span className="sr-only">
          OpenAI API 수동 호출 전용
        </span>
      </button>
      )}
    </div>
  );
};
